import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Cancel as CancelIcon,
  Visibility as PreviewIcon,
  Refresh as RefreshIcon,
  Replay as RetryIcon,
} from '@mui/icons-material';
import {
  previewBackfill,
  startBackfill,
  listBackfillJobs,
  getBackfillJob,
  cancelBackfillJob,
  regenerateRecipeImage,
  listFluxModels,
  BackfillJob,
  BackfillResult,
  BackfillMode,
  BackfillResultStatus,
  FluxModel,
} from '../adminApi';

const POLL_INTERVAL_MS = 5000;

const ImageBackfill: React.FC = () => {
  // Start form state
  const [mode, setMode] = useState<BackfillMode>('missing');
  const [recipeIdsRaw, setRecipeIdsRaw] = useState('');
  const [limit, setLimit] = useState<string>('');
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [starting, setStarting] = useState(false);

  // Jobs list + selected job state
  const [jobs, setJobs] = useState<BackfillJob[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [selectedJob, setSelectedJob] = useState<BackfillJob | null>(null);
  const [results, setResults] = useState<BackfillResult[]>([]);
  const [resultFilter, setResultFilter] = useState<BackfillResultStatus | 'all'>('all');
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [message, setMessage] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [retryingIds, setRetryingIds] = useState<Set<number>>(new Set());

  const [models, setModels] = useState<FluxModel[]>([]);
  const [retryModel, setRetryModel] = useState<string>('');
  const retryModelInfo = models.find(m => m.key === retryModel) || null;

  const fetchJobs = useCallback(async () => {
    try {
      const data = await listBackfillJobs();
      setJobs(data);
      if (!selectedJobId && data.length > 0) {
        setSelectedJobId(data[0].job_id);
      }
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Load jobs failed: ${err.message}` });
    }
  }, [selectedJobId]);

  const fetchJobDetail = useCallback(async () => {
    if (!selectedJobId) return;
    setLoadingDetail(true);
    try {
      const { job, results } = await getBackfillJob(
        selectedJobId,
        resultFilter === 'all' ? undefined : resultFilter
      );
      setSelectedJob(job);
      setResults(results);
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Load job detail failed: ${err.message}` });
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedJobId, resultFilter]);

  useEffect(() => {
    fetchJobs();
    listFluxModels()
      .then(({ models, default: def }) => {
        setModels(models);
        setRetryModel(def);
      })
      .catch(() => {});
  }, [fetchJobs]);

  useEffect(() => {
    fetchJobDetail();
  }, [fetchJobDetail]);

  // Poll while job is running
  useEffect(() => {
    if (!selectedJob || (selectedJob.status !== 'running' && selectedJob.status !== 'pending')) return;
    const id = setInterval(() => {
      fetchJobs();
      fetchJobDetail();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [selectedJob, fetchJobs, fetchJobDetail]);

  const parseIds = (raw: string): number[] | undefined => {
    if (!raw.trim()) return undefined;
    return raw.split(',').map(s => parseInt(s.trim())).filter(n => Number.isFinite(n));
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewCount(null);
    try {
      const ids = parseIds(recipeIdsRaw);
      const result = await previewBackfill({
        mode,
        recipe_ids: ids ? ids.join(',') : undefined,
        limit: limit ? parseInt(limit) : undefined,
      });
      setPreviewCount(result.count);
      setMessage({ severity: 'info', text: `${result.count} recipe(s) match the criteria.` });
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Preview failed: ${err.message}` });
    } finally {
      setPreviewing(false);
    }
  };

  const estimatedCostUsd = (count: number) => {
    // ~$0.025 flux × 1.5 avg attempts + ~$0.001 vision per attempt × 1.5
    return (count * (0.025 * 1.5 + 0.001 * 1.5)).toFixed(2);
  };

  const handleStart = async () => {
    if (previewCount === null) {
      setMessage({ severity: 'error', text: 'Preview first to confirm scope.' });
      return;
    }
    if (!window.confirm(
      `Start backfill for ${previewCount} recipe(s)? ` +
      `Estimated cost: $${estimatedCostUsd(previewCount)}. ` +
      `Existing image_url values will be REPLACED for these recipes.`
    )) return;
    setStarting(true);
    try {
      const ids = parseIds(recipeIdsRaw);
      const result = await startBackfill({
        mode,
        recipe_ids: ids,
        limit: limit ? parseInt(limit) : undefined,
      });
      setMessage({ severity: 'success', text: `Job ${result.job_id} started for ${result.total} recipes.` });
      setSelectedJobId(result.job_id);
      await fetchJobs();
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Start failed: ${err.message}` });
    } finally {
      setStarting(false);
    }
  };

  const handleRetry = async (recipeId: number, resultId: number) => {
    setRetryingIds(prev => new Set(prev).add(recipeId));
    try {
      const result = await regenerateRecipeImage(recipeId, {
        model: retryModel || undefined,
        updateResultId: resultId,
      });
      const passed = (result as any).qa_passed !== false;
      setMessage({
        severity: passed ? 'success' : 'info',
        text: `Recipe ${recipeId} retried with ${retryModelInfo?.label || retryModel}. ${passed ? 'QA passed.' : 'Still failed QA — saved best attempt.'}`,
      });
      await fetchJobDetail();
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Retry failed for recipe ${recipeId}: ${err.response?.data?.message || err.message}` });
    } finally {
      setRetryingIds(prev => {
        const next = new Set(prev);
        next.delete(recipeId);
        return next;
      });
    }
  };

  const handleCancel = async () => {
    if (!selectedJobId) return;
    if (!window.confirm('Request cancel? The job will stop after its current recipe finishes.')) return;
    try {
      await cancelBackfillJob(selectedJobId);
      setMessage({ severity: 'info', text: 'Cancel requested.' });
      await fetchJobDetail();
    } catch (err: any) {
      setMessage({ severity: 'error', text: `Cancel failed: ${err.message}` });
    }
  };

  const jobProgressPct = selectedJob && selectedJob.total_recipes > 0
    ? Math.round((selectedJob.processed_count / selectedJob.total_recipes) * 100)
    : 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Image Backfill</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Production pipeline: generate Flux Pro 1.1 image → Claude Vision QA → retry up to 3× → upload pass to DO Spaces → update recipe.
        Recipes with <code>image_locked = true</code> or <code>flagged_for_review = true</code> are skipped.
      </Typography>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Start panel */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Start a new backfill</Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Mode</InputLabel>
            <Select value={mode} label="Mode" onChange={e => { setMode(e.target.value as BackfillMode); setPreviewCount(null); }}>
              <MenuItem value="missing">Missing images only</MenuItem>
              <MenuItem value="legacy_dalle">Legacy DALL-E images (replace)</MenuItem>
              <MenuItem value="all">All recipes</MenuItem>
              <MenuItem value="specific">Specific recipe IDs</MenuItem>
            </Select>
          </FormControl>
          {mode === 'specific' && (
            <TextField
              size="small"
              label="Recipe IDs (comma-separated)"
              value={recipeIdsRaw}
              onChange={e => { setRecipeIdsRaw(e.target.value); setPreviewCount(null); }}
              sx={{ minWidth: 280 }}
            />
          )}
          <TextField
            size="small"
            label="Limit (optional)"
            type="number"
            value={limit}
            onChange={e => { setLimit(e.target.value); setPreviewCount(null); }}
            sx={{ width: 140 }}
          />
          <Button
            variant="outlined"
            startIcon={previewing ? <CircularProgress size={16} /> : <PreviewIcon />}
            onClick={handlePreview}
            disabled={previewing}
          >
            Preview count
          </Button>
        </Stack>

        {previewCount !== null && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {previewCount} recipe(s) will be processed. Estimated cost: <strong>${estimatedCostUsd(previewCount)}</strong>.
          </Alert>
        )}

        <Button
          variant="contained"
          color="warning"
          startIcon={starting ? <CircularProgress size={16} /> : <StartIcon />}
          onClick={handleStart}
          disabled={starting || previewCount === null || previewCount === 0}
        >
          Start backfill
        </Button>
      </Paper>

      {/* Jobs list + detail */}
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Jobs</Typography>
          <IconButton onClick={() => { fetchJobs(); fetchJobDetail(); }}><RefreshIcon /></IconButton>
        </Stack>

        {jobs.length === 0 ? (
          <Alert severity="info">No backfill jobs yet.</Alert>
        ) : (
          <Tabs
            value={selectedJobId || false}
            onChange={(_, v) => setSelectedJobId(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2 }}
          >
            {jobs.map(j => (
              <Tab
                key={j.job_id}
                value={j.job_id}
                label={`#${j.job_id} ${j.status} (${j.processed_count}/${j.total_recipes})`}
              />
            ))}
          </Tabs>
        )}

        {selectedJob && (
          <Box>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }} alignItems="center">
              <Chip
                color={
                  selectedJob.status === 'completed' ? 'success' :
                  selectedJob.status === 'running' ? 'warning' :
                  selectedJob.status === 'failed' ? 'error' :
                  'default'
                }
                label={`Status: ${selectedJob.status}`}
              />
              <Chip size="small" label={`Total: ${selectedJob.total_recipes}`} />
              <Chip size="small" color="success" label={`Passed: ${selectedJob.passed_count}`} />
              <Chip size="small" color="error" label={`Failed: ${selectedJob.failed_count}`} />
              {(selectedJob.status === 'running' || selectedJob.status === 'pending') && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={selectedJob.cancel_requested}
                >
                  {selectedJob.cancel_requested ? 'Cancel requested…' : 'Cancel'}
                </Button>
              )}
            </Stack>

            <LinearProgress
              variant="determinate"
              value={jobProgressPct}
              sx={{ mb: 2, height: 8, borderRadius: 4 }}
            />

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter results</InputLabel>
                <Select
                  value={resultFilter}
                  label="Filter results"
                  onChange={e => setResultFilter(e.target.value as any)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="success">Success only</MenuItem>
                  <MenuItem value="failed_qa">QA failed only</MenuItem>
                  <MenuItem value="error">Errors only</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 240 }} disabled={models.length === 0}>
                <InputLabel>Retry model</InputLabel>
                <Select value={retryModel} label="Retry model" onChange={e => setRetryModel(e.target.value)}>
                  {models.map(m => (
                    <MenuItem key={m.key} value={m.key}>
                      {m.label} — ${m.costPerImage.toFixed(3)}/img
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                {results.length} result(s) shown · Retry uses the selected model above
              </Typography>
            </Stack>

            {loadingDetail ? (
              <CircularProgress />
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Recipe</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Current image</TableCell>
                      <TableCell>Scores (R / A / Ap)</TableCell>
                      <TableCell>Attempts</TableCell>
                      <TableCell>Issues</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map(r => {
                      // Always prefer the live recipe image — it reflects retries too.
                      const liveUrl = r.current_image_url;
                      const isRetrying = retryingIds.has(r.recipe_id);
                      return (
                        <TableRow key={r.result_id}>
                          <TableCell>
                            <Typography variant="body2">{r.recipe_name}</Typography>
                            <Typography variant="caption" color="text.secondary">#{r.recipe_id}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={r.status === 'success' ? 'success' : r.status === 'failed_qa' ? 'warning' : 'error'}
                              label={r.status}
                            />
                          </TableCell>
                          <TableCell>
                            {liveUrl ? (
                              <Tooltip title="Click to open full size">
                                <a href={liveUrl} target="_blank" rel="noreferrer">
                                  <img
                                    src={liveUrl}
                                    alt={r.recipe_name}
                                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                                  />
                                </a>
                              </Tooltip>
                            ) : '—'}
                          </TableCell>
                          <TableCell>
                            {r.photorealism_score !== null
                              ? `${r.photorealism_score} / ${r.accuracy_score} / ${r.appetizing_score}`
                              : '—'}
                          </TableCell>
                          <TableCell>{r.attempts ?? '—'}</TableCell>
                          <TableCell>
                            <Typography variant="caption">{r.qa_issues || r.error_message || ''}</Typography>
                          </TableCell>
                          <TableCell>
                            {(r.status === 'failed_qa' || r.status === 'error') && (
                              <Tooltip title={`Regenerate with ${retryModelInfo?.label || retryModel} (${retryModelInfo ? `$${retryModelInfo.costPerImage.toFixed(3)}/img` : ''})`}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRetry(r.recipe_id, r.result_id)}
                                    disabled={isRetrying}
                                  >
                                    {isRetrying ? <CircularProgress size={16} /> : <RetryIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ImageBackfill;
